// Every token pairing the UI is allowed to render, declared explicitly.
//
// This list is the subject of contrast.test.ts. Declaring the pairings — rather than
// letting the test cross-multiply every token against every other — is deliberate:
// a cross-product would flood the suite with combinations nobody will ever render
// and force blanket exemptions, and a suite full of exemptions proves nothing.
//
// THE RULE FOR FUTURE WORK: if you render a colour combination that is not in this
// list, add it here first. An unlisted pairing is unverified, and "unverified" is how
// a product that must meet WCAG 2.2 AA (NFR-ACC-001) quietly stops meeting it.

import { AA } from "./contrast";
import { color, focusRing, pastDisplay, palette, stateColor } from "./tokens";

export type ContrastRequirement = "normalText" | "largeText" | "nonText";

export interface Pairing {
  /** Human-readable, used verbatim in the test name so a failure says what broke. */
  readonly name: string;
  readonly foreground: string;
  readonly background: string;
  readonly requirement: ContrastRequirement;
}

export const REQUIRED_RATIO: Record<ContrastRequirement, number> = {
  normalText: AA.normalText,
  largeText: AA.largeText,
  nonText: AA.nonText,
};

const surfaces = [
  ["canvas", color.canvas],
  ["surface", color.surface],
  ["surface-subtle", color.surfaceSubtle],
] as const;

/** Body and supporting text on every surface a page can use. */
const textPairings: Pairing[] = surfaces.flatMap(([surfaceName, background]) => [
  {
    name: `primary text on ${surfaceName}`,
    foreground: color.textPrimary,
    background,
    requirement: "normalText",
  },
  {
    // The plan (21.3) forbids muted text carrying essential information at failing
    // contrast. We hold it to full normal-text AA rather than relying on that
    // judgement call being made correctly on every screen.
    name: `muted text on ${surfaceName}`,
    foreground: color.textMuted,
    background,
    requirement: "normalText",
  },
  {
    name: `accent text/link on ${surfaceName}`,
    foreground: color.accent,
    background,
    requirement: "normalText",
  },
  {
    name: `danger text on ${surfaceName}`,
    foreground: color.danger,
    background,
    requirement: "normalText",
  },
]);

/** Controls: filled buttons, and the outlines that make unfilled ones perceivable. */
const controlPairings: Pairing[] = [
  {
    name: "label on accent button",
    foreground: color.textOnAccent,
    background: color.accent,
    requirement: "normalText",
  },
  {
    name: "label on accent button (hover)",
    foreground: color.textOnAccent,
    background: color.accentHover,
    requirement: "normalText",
  },
  {
    name: "label on danger button",
    foreground: color.textOnAccent,
    background: color.danger,
    requirement: "normalText",
  },
  {
    name: "control outline on surface",
    foreground: color.borderStrong,
    background: color.surface,
    requirement: "nonText",
  },
  {
    name: "control outline on canvas",
    foreground: color.borderStrong,
    background: color.canvas,
    requirement: "nonText",
  },
  {
    name: "focus ring on canvas",
    foreground: color.focus,
    background: color.canvas,
    requirement: "nonText",
  },
  {
    name: "focus ring on surface",
    foreground: color.focus,
    background: color.surface,
    requirement: "nonText",
  },
  {
    // The focus ring never touches a coloured control directly — its white offset
    // does. Testing blue-on-accent would fail (1.05:1) and testing it would be
    // wrong: that pairing is not rendered. What must be perceivable is the offset.
    name: "focus ring offset on accent button",
    foreground: focusRing.offsetColor,
    background: color.accent,
    requirement: "nonText",
  },
  {
    name: "focus ring offset on danger button",
    foreground: focusRing.offsetColor,
    background: color.danger,
    requirement: "nonText",
  },
];

/**
 * State chips. For each of the five states: its label on its own chip background,
 * the chip against the page behind it, and the chip's border.
 */
const statePairings: Pairing[] = Object.entries(stateColor).flatMap(([stateKey, token]) => [
  {
    name: `${stateKey}: chip label on chip background`,
    foreground: token.fg,
    background: token.bg,
    requirement: "normalText",
  },
  {
    name: `${stateKey}: chip border on canvas`,
    foreground: token.border,
    background: color.canvas,
    requirement: "nonText",
  },
  {
    name: `${stateKey}: chip border on surface`,
    foreground: token.border,
    background: color.surface,
    requirement: "nonText",
  },
  {
    // Chips appear inside cards, so the label must also survive the card surface
    // showing through where a chip is rendered borderless.
    name: `${stateKey}: chip label on surface`,
    foreground: token.fg,
    background: color.surface,
    requirement: "normalText",
  },
]);

/** The "Past" display treatment, applied over any state. */
const pastPairings: Pairing[] = [
  {
    name: "past: text on past background",
    foreground: pastDisplay.fg,
    background: pastDisplay.bg,
    requirement: "normalText",
  },
  {
    name: "past: border on canvas",
    foreground: pastDisplay.border,
    background: palette.canvas,
    requirement: "nonText",
  },
];

export const PAIRINGS: readonly Pairing[] = [
  ...textPairings,
  ...controlPairings,
  ...statePairings,
  ...pastPairings,
];
